import type { ClinicClock } from "@/lib/clinic-clock";
import { MUSCLE_KEYS, muscleNames } from "@/lib/face-muscle-names";

import type { MedicationSummary } from "@/types/medication";

export interface DoseDateGroup {
  /** Clinic-local calendar day, or "" for a dose with no date at all. */
  dateKey: string;
  label: string;
  doses: MedicationSummary[];
}

/** When the dose was given, falling back to when it was recorded. */
export function doseMoment(dose: MedicationSummary): string | undefined {
  return dose.doseDate || dose.createdDate || undefined;
}

const momentMs = (dose: MedicationSummary) => {
  const moment = doseMoment(dose);
  return moment ? new Date(moment).getTime() : 0;
};

/** Doses by the clinic day they were given, newest day first, newest dose first within a day. */
export function groupDosesByDate(
  doses: MedicationSummary[],
  formatLabel: (iso: string) => string,
  clock: ClinicClock,
): DoseDateGroup[] {
  const groups = new Map<string, DoseDateGroup>();
  for (const dose of [...doses].sort((a, b) => momentMs(b) - momentMs(a))) {
    const moment = doseMoment(dose);
    const dateKey = moment ? clock.dayOf(moment) : "";
    const existing = groups.get(dateKey);
    if (existing) {
      existing.doses.push(dose);
    } else {
      groups.set(dateKey, {
        dateKey,
        label: moment ? formatLabel(moment) : "Undated",
        doses: [dose],
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (!a.dateKey) return 1;
    if (!b.dateKey) return -1;
    return b.dateKey.localeCompare(a.dateKey);
  });
}

/** The places a dose was given, as the labels the site picker selects. */
export function doseSiteLabels(dose: MedicationSummary): string[] {
  const rows = dose.locations ?? [];
  if (rows.length > 0) return rows.map((row) => row.label);
  const text = dose.administrationLocation?.trim();
  return text ? [text] : [];
}

const addUnique = (target: Map<string, string>, value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return;
  const key = trimmed.toLowerCase();
  if (!target.has(key)) target.set(key, trimmed);
};

/** Sites to offer: the clinic's face chart muscle names, then every site this patient was dosed at. */
export function siteOptions(
  doses: MedicationSummary[],
  renamedMuscles: { muscle: string; label: string }[] | null | undefined,
): string[] {
  const options = new Map<string, string>();
  const names = muscleNames(renamedMuscles);
  for (const key of MUSCLE_KEYS) addUnique(options, names[key]);
  for (const dose of doses) {
    for (const label of doseSiteLabels(dose)) addUnique(options, label);
  }
  return Array.from(options.values());
}

/** Distinct values a free-text dose field already holds across these doses, most used first. */
export function usedValues(
  doses: MedicationSummary[],
  pick: (dose: MedicationSummary) => string | null | undefined,
): string[] {
  const counts = new Map<string, { value: string; count: number }>();
  for (const dose of doses) {
    const value = pick(dose)?.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    const entry = counts.get(key);
    if (entry) entry.count += 1;
    else counts.set(key, { value, count: 1 });
  }
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
    .map((entry) => entry.value);
}
